<script setup lang="ts">
/**
 * CommentThread.vue — board discussion + add comment form.
 *
 * Task 15 (D-P6 cross-slice patch): adds "Send to public submitter" checkbox.
 * The checkbox appears only when:
 *   1. The issue was submitted via the Portal (source === 0, i.e. SourceKind.Portal)
 *   2. The issue has a linked PublicSubmitterEmailLink (hasPortalSubmitter === true)
 *
 * When the checkbox is checked + comment is submitted, isPublicFacing: true is
 * included in AddCommentRequest so the API fires SendCommentToSubmitter email.
 * A transient toast is shown after the comment is saved with isPublicFacing.
 */
import { ref, computed } from 'vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import CommentItem from './CommentItem.vue';
import type { components } from '@/api/generated/schema';

type CommentDto = components['schemas']['CommentDto'];
type AddCommentRequest = components['schemas']['AddCommentRequest'];

/** SourceKind.Portal = 0 per issue-enums.ts */
const SOURCE_PORTAL = 0;

interface Props {
  comments: CommentDto[];
  isSubmitting?: boolean;
  /** SourceKind numeric value for this issue — used to conditionally show IsPublicFacing */
  issueSource?: number;
  /** True when the issue has a linked portal submitter email (PublicSubmitterEmailLink exists) */
  hasPortalSubmitter?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  submit: [req: AddCommentRequest];
}>();

const body = ref('');
const notifyAllBoard = ref(false);
const isPublicFacing = ref(false);

/** Show the "Send to public submitter" checkbox */
const showPublicFacingCheckbox = computed(
  () => props.issueSource === SOURCE_PORTAL && props.hasPortalSubmitter === true,
);

/** Transient toast: shown briefly after a public-facing comment is submitted */
const showPublicFacingToast = ref(false);
let toastTimer: ReturnType<typeof setTimeout> | null = null;

function onSubmit() {
  if (!body.value.trim()) return;

  const wasPublicFacing = isPublicFacing.value;

  emit('submit', {
    body: body.value.trim(),
    notifyAllBoard: notifyAllBoard.value,
    isPublicFacing: wasPublicFacing,
  });

  // Show toast if this was a public-facing comment
  if (wasPublicFacing) {
    showPublicFacingToast.value = true;
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      showPublicFacingToast.value = false;
    }, 4000);
  }

  body.value = '';
  notifyAllBoard.value = false;
  isPublicFacing.value = false;
}
</script>

<template>
  <div data-testid="comment-thread">
    <!-- Comments list -->
    <ul v-if="comments.length > 0" class="divide-y divide-border">
      <CommentItem v-for="c in comments" :key="c.id" :comment="c" />
    </ul>
    <p v-else class="text-caption text-muted py-4">No board discussion yet.</p>

    <!-- Add comment form -->
    <form class="mt-4 flex flex-col gap-3" @submit.prevent="onSubmit">
      <textarea
        v-model="body"
        rows="3"
        placeholder="Add a board note…"
        class="w-full px-3 py-2 text-body bg-surface border border-border rounded-md outline-none focus:ring-2 focus:ring-navy-600/20 focus:border-navy-600 resize-none"
        data-testid="comment-input"
      />
      <div class="flex flex-col gap-2">
        <!-- Notify all board -->
        <label class="flex items-center gap-2 text-caption text-muted cursor-pointer">
          <input v-model="notifyAllBoard" type="checkbox" class="rounded">
          Notify all board members
        </label>

        <!-- IsPublicFacing (D-P6) — shown only for portal issues with a submitter -->
        <label
          v-if="showPublicFacingCheckbox"
          class="flex items-center gap-2 text-caption cursor-pointer"
          :class="isPublicFacing ? 'text-navy-700 font-medium' : 'text-muted'"
          data-testid="public-facing-label"
        >
          <input
            v-model="isPublicFacing"
            type="checkbox"
            class="rounded"
            data-testid="public-facing-checkbox"
          >
          Send to public submitter
        </label>
      </div>

      <div class="flex items-center justify-between gap-3">
        <div />
        <BaseButton
          variant="secondary"
          size="sm"
          type="submit"
          :loading="isSubmitting"
          :disabled="!body.trim()"
        >
          Add note
        </BaseButton>
      </div>
    </form>

    <!-- Toast: email sent to portal submitter -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="showPublicFacingToast"
        class="mt-2 px-3 py-2 bg-green-50 border border-green-200 rounded-md text-caption text-green-700 flex items-center gap-2"
        role="status"
        data-testid="public-facing-toast"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 flex-shrink-0">
          <path d="M20 6L9 17l-5-5" />
        </svg>
        Email sent to the portal submitter.
      </div>
    </Transition>
  </div>
</template>
